import { z } from "zod"
import { foldBundleEventBindings } from "@cogo/publisher-core/event-binding-shape"
import {
  effectiveStepType,
  isActionStep,
} from "@cogo/publisher-core/flow-step-shape"

/**
 * Browser-safe Agent Surface contract.
 *
 * This module intentionally imports only browser-safe contract helpers. It must
 * remain free of the A2A server, QuickJS, Express, Oxigraph, Node builtins, and
 * Gateway registry code so Studio can consume exactly the same closed IDs and
 * admission boundary.
 */

export const AGENT_SURFACE_SCENARIOS = {
  applicationDashboard: {
    id: "application.dashboard",
    surfaceKind: "application",
  },
  applicationForm: {
    id: "application.form",
    surfaceKind: "application",
  },
  applicationDataTable: {
    id: "application.data-table",
    surfaceKind: "application",
  },
  applicationApproval: {
    id: "application.approval",
    surfaceKind: "application",
  },
  applicationEmpty: {
    id: "application.empty",
    surfaceKind: "application",
  },
  applicationLoading: {
    id: "application.loading",
    surfaceKind: "application",
  },
  applicationErrorRetry: {
    id: "application.error-retry",
    surfaceKind: "application",
  },
  documentReport: {
    id: "document.report",
    surfaceKind: "document",
  },
  documentChecklist: {
    id: "document.checklist",
    surfaceKind: "document",
  },
  documentProposal: {
    id: "document.proposal",
    surfaceKind: "document",
  },
} as const

export type AgentSurfaceScenarioId =
  (typeof AGENT_SURFACE_SCENARIOS)[keyof typeof AGENT_SURFACE_SCENARIOS]["id"]
export type AgentSurfaceKind = "application" | "document"

export const AGENT_SURFACE_SCENARIO_MANIFEST = Object.freeze(
  Object.values(AGENT_SURFACE_SCENARIOS),
)

export const AGENT_SURFACE_SCENARIO_IDS = Object.freeze(
  AGENT_SURFACE_SCENARIO_MANIFEST.map((scenario) => scenario.id),
) as readonly AgentSurfaceScenarioId[]

export const agentSurfaceScenarioIdSchema = z.custom<AgentSurfaceScenarioId>(
  (value) =>
    AGENT_SURFACE_SCENARIO_IDS.includes(value as AgentSurfaceScenarioId),
  { message: "unknown agent surface scenario" },
)

export const AGENT_SURFACE_MAX_NODES = 100
export const AGENT_SURFACE_MAX_DEPTH = 12
export const AGENT_SURFACE_MAX_FLOWS = 50
export const AGENT_SURFACE_MAX_STEPS_PER_FLOW = 25
export const AGENT_SURFACE_MAX_OUTPUT_BYTES = 256 * 1024
export const COGO_SURFACE_PROPOSAL_SCHEMA_VERSION =
  "cogo.agent-surface-proposal.v1" as const

/**
 * The approval contract every surface proposal carries — ONE stamp site.
 *
 * There is more than one KIND of proposal now: the agent-surface proposal a
 * QuickJS guest program produces (`cogo-surface-proposal.ts`) and the
 * generated-application proposal the browser produces from a platform
 * generation result (`generated-application-proposal.ts`). Both must say the
 * same thing about authority, and both used to be free to say it in their own
 * words.
 *
 * Stated once here so that flipping `mayPatchCogoJsonDirectly` reddens BOTH
 * lanes, not one. A second producer that spelled these literals inline would
 * make this constant decorative on exactly the lane the flip is meant to test:
 * the mutation would pass while the new path kept its own `false`.
 *
 * The `z.literal` pins in the two schemas are the other half. The constant
 * makes the value single-sourced; the literals make a wrong value unbuildable.
 */
export const AGENT_SURFACE_APPROVAL_CONTRACT = Object.freeze({
  requiresApproval: true,
  mayPatchCogoJsonDirectly: false,
} as const)

const dangerousKeys = new Set(["__proto__", "prototype", "constructor"])
const previewFlowContainerKeys = new Set(["flows"])
const previewFlowKeys = new Set(["trigger", "steps"])
const previewStepKeys = new Set(["type", "action", "params"])
const previewSetStateParamKeys = new Set(["target", "value"])
const previewBindingKeys = new Set(["flow"])
const previewNodeTypes = new Set([
  "container",
  "text",
  "input",
  "textarea",
  "button",
  "checkbox",
])
const previewNodeKeys = new Set([
  "id",
  "type",
  "text",
  "className",
  "children",
  "events",
  "inputType",
  "label",
  "ariaLabel",
  "placeholder",
  "required",
  "bind",
  "visible",
])
const previewInputTypes = new Set(["text", "email", "number", "date", "search"])
const staticLocalSegment = /^[A-Za-z_][A-Za-z0-9_-]*$/

const interactionHintSchema = z
  .object({
    event: z.string().min(1).max(128),
    description: z.string().min(1).max(500),
  })
  .strict()

export const cogoSurfaceProposalGuestOutputSchema = z
  .object({
    surface: z
      .object({
        kind: z.enum(["application", "document"]),
        baseRevision: z.string().min(1).max(256),
        cogoJson: z.record(z.unknown()),
      })
      .strict(),
    summary: z.string().min(1).max(1_000),
    interactionHints: z.array(interactionHintSchema).max(20),
  })
  .strip()

export const cogoSurfaceProposalSchema = z
  .object({
    schemaVersion: z.literal(COGO_SURFACE_PROPOSAL_SCHEMA_VERSION),
    proposalId: z.string().min(1).max(256),
    agent: z
      .object({
        agentId: z.string().min(1).max(256),
        conversationId: z.string().min(1).max(256),
        runId: z.string().min(1).max(256),
      })
      .strict(),
    surface: cogoSurfaceProposalGuestOutputSchema.shape.surface,
    summary: cogoSurfaceProposalGuestOutputSchema.shape.summary,
    interactionHints: cogoSurfaceProposalGuestOutputSchema.shape.interactionHints,
    requiresApproval: z.literal(true),
    mayPatchCogoJsonDirectly: z.literal(false),
  })
  .strict()

export type CogoSurfaceProposalGuestOutput = z.infer<
  typeof cogoSurfaceProposalGuestOutputSchema
>
export type CogoSurfaceProposal = z.infer<typeof cogoSurfaceProposalSchema>

export interface AgentSurfaceProposalExpectation {
  scenarioId: AgentSurfaceScenarioId
  surfaceKind: AgentSurfaceKind
  baseRevision: string
}

function reject(message: string): never {
  throw new Error(`Agent surface admission rejected guest output: ${message}`)
}

/**
 * The JSON floor, shared with the generated-application admission.
 *
 * Prototypes, cycles, accessors, `__proto__`/`prototype`/`constructor` keys,
 * sparse arrays and non-finite numbers are refused here for BOTH kinds of
 * proposal. Nothing about a surface's provenance makes any of them safe, so
 * this is the one part of admission the two kinds share outright rather than
 * each restating.
 */
export function assertAgentSurfaceSafeJson(value: unknown): void {
  assertSafeJson(value)
}

export function isPlainJsonObject(
  value: unknown,
): value is Record<string, unknown> {
  return (
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value) &&
    Object.getPrototypeOf(value) === Object.prototype
  )
}

function isArrayIndex(key: string): boolean {
  const index = Number(key)
  return (
    Number.isInteger(index) &&
    index >= 0 &&
    index < 2 ** 32 - 1 &&
    String(index) === key
  )
}

function assertSafeJson(value: unknown, ancestors = new WeakSet<object>()): void {
  if (
    value === null ||
    typeof value === "string" ||
    typeof value === "boolean"
  ) {
    return
  }
  if (typeof value === "number") {
    if (!Number.isFinite(value)) reject("non-finite numbers are not JSON values")
    return
  }
  if (
    typeof value === "undefined" ||
    typeof value === "function" ||
    typeof value === "symbol" ||
    typeof value === "bigint"
  ) {
    reject(`${typeof value} values are not JSON values`)
  }
  if (typeof value !== "object") reject("unsupported JSON value")
  if (ancestors.has(value)) reject("cyclic values are not allowed")

  const isArray = Array.isArray(value)
  if (
    isArray
      ? Object.getPrototypeOf(value) !== Array.prototype
      : !isPlainJsonObject(value)
  ) {
    reject("custom prototypes are not allowed")
  }

  const ownPropertyNames = Object.getOwnPropertyNames(value)
  for (const key of ownPropertyNames) {
    if (dangerousKeys.has(key)) reject(`dangerous key ${key} is not allowed`)
    const descriptor = Object.getOwnPropertyDescriptor(value, key)
    if (!descriptor || "get" in descriptor || "set" in descriptor) {
      reject("accessor properties are not JSON values")
    }
    if (isArray && key !== "length" && !isArrayIndex(key)) {
      reject("non-index array properties are not JSON values")
    }
  }
  if (Object.getOwnPropertySymbols(value).length > 0) {
    reject("symbol keys are not JSON values")
  }

  ancestors.add(value)
  if (isArray) {
    for (let index = 0; index < value.length; index += 1) {
      if (!Object.hasOwn(value, index)) reject("sparse arrays are not allowed")
      assertSafeJson(value[index], ancestors)
    }
  } else {
    for (const key of ownPropertyNames) {
      const descriptor = Object.getOwnPropertyDescriptor(value, key)
      assertSafeJson(descriptor!.value, ancestors)
    }
  }
  ancestors.delete(value)
}

function readObject(value: unknown, path: string): Record<string, unknown> {
  if (!isPlainJsonObject(value)) reject(`${path} must be a plain object`)
  return value
}

function readOptionalObject(
  value: unknown,
  path: string,
): Record<string, unknown> {
  return value === undefined ? {} : readObject(value, path)
}

function assertOnlyKeys(
  value: Record<string, unknown>,
  allowed: ReadonlySet<string>,
  path: string,
): void {
  for (const key of Object.keys(value)) {
    if (!allowed.has(key)) reject(`${path} field ${key} is not allowed`)
  }
}

function cloneSafeJson<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T
}

function readBindingFlow(binding: unknown): {
  flowName: string
  projection: string | { flow: string }
} {
  if (typeof binding === "string" && binding.length > 0) {
    return { flowName: binding, projection: binding }
  }
  const record = readObject(binding, "event binding")
  assertOnlyKeys(record, previewBindingKeys, "event binding")
  if (typeof record.flow !== "string" || record.flow.length === 0) {
    reject("event binding requires a non-empty flow")
  }
  return { flowName: record.flow, projection: { flow: record.flow } }
}

function validateStaticLocalTarget(
  target: unknown,
  localState: Record<string, unknown>,
  flowName: string,
): string {
  if (typeof target !== "string") {
    reject(`preview flow ${flowName} may only write local.* state`)
  }
  const segments = target.split(".")
  if (
    segments.length < 2 ||
    segments[0] !== "local" ||
    segments.slice(1).some(
      (segment) =>
        !segment || dangerousKeys.has(segment) || !staticLocalSegment.test(segment),
    )
  ) {
    reject(`preview flow ${flowName} requires a static safe local.* target`)
  }
  if (!Object.hasOwn(localState, segments[1])) {
    reject(`preview flow ${flowName} target ${target} is not declared in state.localState`)
  }
  return target
}

function validatePreviewLocalFlows(
  cogoJson: Record<string, unknown>,
): Map<string, Record<string, unknown>> {
  const flowContainer = readOptionalObject(cogoJson.flow, "surface.cogoJson.flow")
  assertOnlyKeys(
    flowContainer,
    previewFlowContainerKeys,
    "surface.cogoJson.flow",
  )
  const flows = readOptionalObject(
    flowContainer.flows,
    "surface.cogoJson.flow.flows",
  )
  const state = readOptionalObject(cogoJson.state, "surface.cogoJson.state")
  const localState = readOptionalObject(
    state.localState,
    "surface.cogoJson.state.localState",
  )
  const validated = new Map<string, Record<string, unknown>>()
  const flowEntries = Object.entries(flows)

  if (flowEntries.length > AGENT_SURFACE_MAX_FLOWS) {
    reject(`preview flow count exceeds ${AGENT_SURFACE_MAX_FLOWS}`)
  }

  for (const [flowName, flowValue] of flowEntries) {
    if (!flowName) reject("flow names must be non-empty")
    const flow = readObject(flowValue, `flow ${flowName}`)
    assertOnlyKeys(flow, previewFlowKeys, `preview flow ${flowName}`)
    if (flow.trigger !== "event") {
      reject(`preview flow ${flowName} must use the event trigger`)
    }
    if (!Array.isArray(flow.steps) || flow.steps.length === 0) {
      reject(`preview flow ${flowName} requires at least one step`)
    }
    if (flow.steps.length > AGENT_SURFACE_MAX_STEPS_PER_FLOW) {
      reject(
        `preview flow ${flowName} step count exceeds ${AGENT_SURFACE_MAX_STEPS_PER_FLOW}`,
      )
    }
    const steps: Record<string, unknown>[] = []
    for (const stepValue of flow.steps) {
      const step = readObject(stepValue, `flow ${flowName} step`)
      assertOnlyKeys(step, previewStepKeys, `preview flow ${flowName} step`)
      const normalizedStepType = effectiveStepType(step)
      if (!isActionStep(step) || normalizedStepType !== "basic") {
        reject(`preview flow ${flowName} may only use basic steps`)
      }
      if (step.action !== "setState") {
        reject(`preview flow ${flowName} may only use setState`)
      }
      const params = readObject(step.params, `flow ${flowName} setState params`)
      assertOnlyKeys(
        params,
        previewSetStateParamKeys,
        `preview flow ${flowName} setState params`,
      )
      if (!Object.hasOwn(params, "value")) {
        reject(`preview flow ${flowName} setState params require value`)
      }
      const target = validateStaticLocalTarget(params.target, localState, flowName)
      steps.push({
        ...(step.type === undefined ? {} : { type: normalizedStepType }),
        action: "setState",
        params: { target, value: cloneSafeJson(params.value) },
      })
    }
    validated.set(flowName, { trigger: "event", steps })
  }

  return validated
}

type NodeEventReference = {
  nodeId: string
  eventType: string
  flowName: string
}

function validateNodeTree(root: unknown): {
  nodeIds: Set<string>
  nodeEvents: NodeEventReference[]
  root: Record<string, unknown>
} {
  const nodeIds = new Set<string>()
  const nodeEvents: NodeEventReference[] = []
  const visit = (value: unknown, depth: number): Record<string, unknown> => {
    if (depth > AGENT_SURFACE_MAX_DEPTH) {
      reject(`UI depth exceeds ${AGENT_SURFACE_MAX_DEPTH}`)
    }
    if (nodeIds.size >= AGENT_SURFACE_MAX_NODES) {
      reject(`UI node count exceeds ${AGENT_SURFACE_MAX_NODES}`)
    }

    const node = readObject(value, "surface.cogoJson.ui.root node")
    const id = node.id
    if (typeof id !== "string" || id.length === 0) {
      reject("every UI node requires a non-empty string id")
    }
    if (nodeIds.has(id)) reject(`duplicate UI node id ${id}`)
    nodeIds.add(id)

    assertOnlyKeys(node, previewNodeKeys, `node ${id}`)
    if (typeof node.type !== "string" || !previewNodeTypes.has(node.type)) {
      reject(`node ${id} type is not allowed in Agent Surface preview`)
    }
    for (const key of [
      "text",
      "className",
      "inputType",
      "label",
      "ariaLabel",
      "placeholder",
      "bind",
    ] as const) {
      if (key in node && typeof node[key] !== "string") {
        reject(`node ${key} must be a string when present`)
      }
    }
    if (
      "inputType" in node &&
      !previewInputTypes.has(node.inputType as string)
    ) {
      reject(`node ${id} inputType is not allowed in Agent Surface preview`)
    }
    if ("required" in node && typeof node.required !== "boolean") {
      reject(`node ${id} required must be a boolean when present`)
    }
    if (
      "visible" in node &&
      typeof node.visible !== "boolean" &&
      typeof node.visible !== "string"
    ) {
      reject(`node ${id} visible must be a boolean or string when present`)
    }

    let projectedEvents: Record<string, string> | undefined
    if ("events" in node) {
      const events = readObject(node.events, "node events")
      projectedEvents = {}
      for (const [eventType, reference] of Object.entries(events)) {
        if (!eventType || typeof reference !== "string" || !reference) {
          reject("node event must reference a non-empty flow name")
        }
        nodeEvents.push({ nodeId: id, eventType, flowName: reference })
        projectedEvents[eventType] = reference
      }
    }

    if ("children" in node && !Array.isArray(node.children)) {
      reject("node children must be an array when present")
    }
    const projectedChildren = Array.isArray(node.children)
      ? node.children.map((child) => visit(child, depth + 1))
      : undefined
    const projection: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(node)) {
      if (key === "children" || key === "events") continue
      projection[key] = cloneSafeJson(value)
    }
    if (projectedEvents) projection.events = projectedEvents
    if (projectedChildren) projection.children = projectedChildren
    return projection
  }

  const projectedRoot = visit(root, 1)
  return { nodeIds, nodeEvents, root: projectedRoot }
}

function validateBindings(
  cogoJson: Record<string, unknown>,
  nodeIds: ReadonlySet<string>,
  nodeEvents: readonly NodeEventReference[],
  flows: ReadonlyMap<string, Record<string, unknown>>,
): {
  declaredFlows: Set<string>
  bindings: Record<string, string | { flow: string }>
} {
  const events = readOptionalObject(cogoJson.events, "surface.cogoJson.events")
  const bindings = foldBundleEventBindings<unknown>(events)
  const boundFlows = new Set<string>()
  const bindingFlowByKey = new Map<string, string>()
  const projectedBindings: Record<string, string | { flow: string }> = {}

  for (const [key, binding] of Object.entries(bindings)) {
    const separator = key.lastIndexOf(":")
    if (separator <= 0 || separator === key.length - 1) {
      reject(`invalid event binding key ${key}`)
    }
    const nodeId = key.slice(0, separator)
    if (!nodeIds.has(nodeId)) {
      reject(`event binding ${key} references an unknown node`)
    }
    const { flowName, projection } = readBindingFlow(binding)
    if (!flows.has(flowName)) {
      reject(`event binding ${key} references missing flow ${flowName}`)
    }
    bindingFlowByKey.set(key, flowName)
    boundFlows.add(flowName)
    projectedBindings[key] = projection
  }

  for (const event of nodeEvents) {
    const key = `${event.nodeId}:${event.eventType}`
    if (bindingFlowByKey.get(key) !== event.flowName) {
      reject(`node event ${key} requires a matching events.bindings flow`)
    }
  }

  for (const flowName of flows.keys()) {
    if (!boundFlows.has(flowName)) {
      reject(`preview flow ${flowName} requires an event binding`)
    }
  }

  return { declaredFlows: boundFlows, bindings: projectedBindings }
}

function validateInteractionHints(
  value: unknown,
  declaredFlows: ReadonlySet<string>,
): void {
  if (!Array.isArray(value)) return
  for (const hint of value) {
    if (!isPlainJsonObject(hint)) continue
    if (typeof hint.event !== "string" || !declaredFlows.has(hint.event)) {
      reject("interaction hint must reference a declared event")
    }
  }
}

function utf8ByteLength(value: string): number {
  return new TextEncoder().encode(value).byteLength
}

function assertSerializedSize(value: unknown): void {
  const serialized = JSON.stringify(value)
  if (utf8ByteLength(serialized) > AGENT_SURFACE_MAX_OUTPUT_BYTES) {
    reject(`serialized guest output exceeds ${AGENT_SURFACE_MAX_OUTPUT_BYTES} bytes`)
  }
}

function assertRawSize(value: string, label: string): void {
  if (utf8ByteLength(value) > AGENT_SURFACE_MAX_OUTPUT_BYTES) {
    throw new Error(
      `Agent surface raw ${label} exceeds ${AGENT_SURFACE_MAX_OUTPUT_BYTES} bytes`,
    )
  }
}

export function isAgentSurfaceScenarioId(
  value: unknown,
): value is AgentSurfaceScenarioId {
  return AGENT_SURFACE_SCENARIO_IDS.includes(value as AgentSurfaceScenarioId)
}

export function agentSurfaceKindForScenarioId(
  scenarioId: AgentSurfaceScenarioId,
): AgentSurfaceKind {
  const definition = AGENT_SURFACE_SCENARIO_MANIFEST.find(
    (candidate) => candidate.id === scenarioId,
  )
  if (!definition) reject(`unknown agent surface scenario ${scenarioId}`)
  return definition.surfaceKind
}

export function admitAgentSurfaceCogoJson(
  value: unknown,
  interactionHints: unknown = [],
): Record<string, unknown> {
  assertSafeJson(value)
  const cogoJson = readObject(value, "surface.cogoJson")
  const ui = readObject(cogoJson.ui, "surface.cogoJson.ui")
  if (!("root" in ui)) reject("surface.cogoJson.ui.root is required")

  const { nodeIds, nodeEvents, root } = validateNodeTree(ui.root)
  const flows = validatePreviewLocalFlows(cogoJson)
  const { declaredFlows, bindings } = validateBindings(
    cogoJson,
    nodeIds,
    nodeEvents,
    flows,
  )
  validateInteractionHints(interactionHints, declaredFlows)
  assertSerializedSize(value)

  const admitted = cloneSafeJson(cogoJson)
  const admittedUi = readObject(admitted.ui, "surface.cogoJson.ui")
  admittedUi.root = root
  if (cogoJson.flow !== undefined) {
    admitted.flow = { flows: Object.fromEntries(flows) }
  }
  if (cogoJson.events !== undefined) {
    const admittedEvents = readObject(admitted.events, "surface.cogoJson.events")
    admittedEvents.bindings = bindings
  }
  return admitted
}

export function admitAgentSurfaceGuestOutput(
  value: unknown,
): CogoSurfaceProposalGuestOutput {
  assertSafeJson(value)
  const guestOutput = readObject(value, "guest output")
  const surface = readObject(guestOutput.surface, "surface")
  const admittedCogoJson = admitAgentSurfaceCogoJson(
    surface.cogoJson,
    guestOutput.interactionHints,
  )
  assertSerializedSize(value)
  const admitted = cogoSurfaceProposalGuestOutputSchema.parse(value)
  admitted.surface.cogoJson = admittedCogoJson
  return admitted
}

export function admitCogoSurfaceProposal(
  value: unknown,
  expectation: AgentSurfaceProposalExpectation,
): CogoSurfaceProposal {
  assertSafeJson(value)
  const proposal = cogoSurfaceProposalSchema.parse(value)
  const admittedCogoJson = admitAgentSurfaceCogoJson(
    proposal.surface.cogoJson,
    proposal.interactionHints,
  )
  proposal.surface.cogoJson = admittedCogoJson

  const meta = readObject(
    proposal.surface.cogoJson.meta,
    "surface.cogoJson.meta",
  )
  const scenarioId = meta.agentSurfaceScenarioId
  if (!isAgentSurfaceScenarioId(scenarioId)) {
    reject("surface metadata requires a known agentSurfaceScenarioId")
  }
  const expectedKind = agentSurfaceKindForScenarioId(scenarioId)
  if (proposal.surface.kind !== expectedKind) {
    reject(`scenario ${scenarioId} requires ${expectedKind} surface kind`)
  }
  if (scenarioId !== expectation.scenarioId) {
    reject(
      `scenario metadata mismatch: expected ${expectation.scenarioId}, received ${scenarioId}`,
    )
  }
  if (
    proposal.surface.kind !== expectation.surfaceKind
  ) {
    reject(
      `surface kind mismatch: expected ${expectation.surfaceKind}, received ${proposal.surface.kind}`,
    )
  }
  if (
    proposal.surface.baseRevision !== expectation.baseRevision
  ) {
    reject(
      `base revision mismatch: expected ${expectation.baseRevision}, received ${proposal.surface.baseRevision}`,
    )
  }

  assertSerializedSize(value)
  return proposal
}

export function parseAgentSurfaceGuestOutputText(
  value: string,
): CogoSurfaceProposalGuestOutput {
  assertRawSize(value, "guest output")
  let parsed: unknown
  try {
    parsed = JSON.parse(value)
  } catch {
    throw new Error("QuickJS agent program returned invalid JSON")
  }
  return admitAgentSurfaceGuestOutput(parsed)
}

export function parseCogoSurfaceProposalText(
  value: string,
  expectation: AgentSurfaceProposalExpectation,
): CogoSurfaceProposal {
  assertRawSize(value, "proposal artifact")
  let parsed: unknown
  try {
    parsed = JSON.parse(value)
  } catch {
    throw new Error("Agent surface proposal artifact returned invalid JSON")
  }
  return admitCogoSurfaceProposal(parsed, expectation)
}
